// Parsing for the web Vibe game → app handoff (see functions/vibeWebGame.js
// and Tonyte_website/src/play/handoff.js for the producing side).
//
//   https://go.tonyte.app/vibe/<sessionId>.<secret>   claim link (ref)
//   https://go.tonyte.app/vibe/code/<CODE>            claim link (code)
//   Play install referrer: tonyte_vibe_ref=<ref> | tonyte_vibe_code=<CODE>
//
// go.tonyte.app is deliberately NOT tonyte.app: iOS won't open a Universal
// Link to the domain the user is already browsing, and older installed builds
// don't claim go.tonyte.app at all, so they can't swallow these links.

export type VibeClaimCredential = { ref: string } | { code: string };

export const VIBE_CLAIM_HOST = 'go.tonyte.app';
const REF_RE = /^[A-Za-z0-9]{20}\.[A-Za-z0-9_-]{43}$/;
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/** "abcd efgh" / "ABCD-EFGH" → "ABCD-EFGH", or null if it can't be a code. */
export function normalizeRecoveryCode(input: string): string | null {
  const cleaned = String(input || '').toUpperCase().replace(/[\s-]/g, '');
  if (cleaned.length !== 8) return null;
  for (const ch of cleaned) if (!CODE_ALPHABET.includes(ch)) return null;
  return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
}

export function isValidRef(ref: string): boolean {
  return REF_RE.test(ref);
}

export function parseVibeClaimUrl(url: string): VibeClaimCredential | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.host !== VIBE_CLAIM_HOST) return null;
  const parts = parsed.pathname.split('/').filter(Boolean).map(part => {
    try {
      return decodeURIComponent(part);
    } catch {
      return part;
    }
  });
  if (parts[0] !== 'vibe') return null;
  if (parts[1] === 'code' && parts.length === 3) {
    const code = normalizeRecoveryCode(parts[2]);
    return code ? { code } : null;
  }
  if (parts.length === 2 && isValidRef(parts[1])) return { ref: parts[1] };
  return null;
}

/** Google Play install referrer string, e.g. "tonyte_vibe_ref=…&utm_source=…". */
export function parseInstallReferrer(referrer: string | null | undefined): VibeClaimCredential | null {
  if (!referrer) return null;
  let decoded = referrer;
  // Play normally hands back the decoded value; tolerate a still-encoded one.
  if (!decoded.includes('=') && decoded.includes('%3D')) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return null;
    }
  }
  const params: Record<string, string> = {};
  decoded.split('&').forEach(pair => {
    const [key, ...rest] = pair.split('=');
    if (!key) return;
    try {
      params[decodeURIComponent(key)] = decodeURIComponent(rest.join('='));
    } catch {
      // ignore malformed pair
    }
  });
  if (params.tonyte_vibe_ref && isValidRef(params.tonyte_vibe_ref)) return { ref: params.tonyte_vibe_ref };
  const code = params.tonyte_vibe_code ? normalizeRecoveryCode(params.tonyte_vibe_code) : null;
  return code ? { code } : null;
}

export function sameCredential(a: VibeClaimCredential | null | undefined, b: VibeClaimCredential | null | undefined): boolean {
  if (!a || !b) return false;
  if ('ref' in a && 'ref' in b) return a.ref === b.ref;
  if ('code' in a && 'code' in b) return a.code === b.code;
  return false;
}
